import { LOAD_NUMBER } from 'config'
import { Feedback, Post, PostFeedback } from 'lib/_types'
import { yayApi } from '../baseApi'
import {
  mapPostGroupStatuses,
  mapPostThreadStatuses,
  mapPostFeedbacks,
} from './timeline.helper'
import {
  pathApiFeedbacks,
  pathApiPostsFollowingTimeline,
  pathApiPostsGroupPinnedPost,
  pathApiPostsGroupTimeline,
  pathApiPostsNoReplyTimeline,
  pathApiPostsPinnedPost,
  pathApiPostsPinnedPostShow,
  pathApiPostsPublicTimeline,
  pathApiPostsRecommendedTimeline,
  pathApiPostsTags,
  pathApiPostsTimeline,
  pathApiPostsUserTimeline,
  pathApiToolsPostsTimeline,
} from 'lib/paths'

export const timelineApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'PublicTimeline',
      'NoReplyPublicTimeline',
      'FollowingTimeline',
      'GroupTimeline',
      'GroupTimelinePinned',
      'UserTimeline',
      'UserTimelinePinned',
      'HashtagTimeline',
      'HotPostTimeline',
      'RecommendedTimeline',
      'TimelineTestingTools',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getPublicTimeline: build.query<
        Array<Post>,
        {
          fromPostId: string
          isLoggedIn?: boolean
          sharedInterests?: boolean
          experimentOlderAgeRules?: boolean
          customGenerationRange: boolean
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'PublicTimeline', id: args?.fromPostId }]
        },
        async queryFn(
          {
            fromPostId,
            isLoggedIn,
            sharedInterests,
            experimentOlderAgeRules,
            customGenerationRange,
          },
          _api,
          _extraOptions,
          baseQuery
        ) {
          const timelineRes = await baseQuery({
            url: isLoggedIn ? pathApiPostsTimeline : pathApiPostsPublicTimeline,
            params: {
              number: LOAD_NUMBER,
              ...(fromPostId != '0' && { from_post_id: fromPostId }),
              ...(isLoggedIn &&
                typeof sharedInterests === 'boolean' && {
                  shared_interest_categories: Boolean(sharedInterests),
                }),
              ...(isLoggedIn && {
                experiment_older_age_rules: Boolean(experimentOlderAgeRules),
              }),
              ...(typeof customGenerationRange === 'boolean' && {
                custom_generation_range: customGenerationRange,
              }),
            },
          })

          const posts = (timelineRes?.data?.posts ?? []) as Post[]

          const postsWithGroupRelationship = await mapPostGroupStatuses({
            baseQuery,
            posts,
            isLoggedIn,
          })

          return {
            data: postsWithGroupRelationship,
          }
        },
        keepUnusedDataFor: 0,
      }),

      getNoReplyPublicTimeline: build.query<
        Array<Post>,
        {
          fromPostId: string
          sharedInterests?: boolean
          experimentOlderAgeRules?: boolean
          customGenerationRange: boolean
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'NoReplyPublicTimeline', id: args?.fromPostId }]
        },
        async queryFn(
          {
            fromPostId,
            sharedInterests,
            experimentOlderAgeRules,
            customGenerationRange,
          },
          _api,
          _extraOptions,
          baseQuery
        ) {
          const timelineRes = await baseQuery({
            url: pathApiPostsNoReplyTimeline,
            params: {
              number: LOAD_NUMBER,
              ...(fromPostId != '0' && { from_post_id: fromPostId }),
              ...(typeof sharedInterests === 'boolean' && {
                shared_interest_categories: sharedInterests,
              }),
              ...(typeof experimentOlderAgeRules === 'boolean' && {
                experiment_older_age_rules: experimentOlderAgeRules,
              }),
              ...(typeof customGenerationRange === 'boolean' && {
                custom_generation_range: customGenerationRange,
              }),
            },
          })

          const posts = (timelineRes?.data?.posts ?? []) as Post[]

          const postsWithGroupRelationship = await mapPostGroupStatuses({
            baseQuery,
            posts,
          })

          return {
            data: postsWithGroupRelationship,
          }
        },
        keepUnusedDataFor: 0,
      }),

      getFollowingTimeline: build.query<
        Array<Post>,
        {
          hideReplies: boolean
          fromPostId: string
          hiddenWords: Array<string>
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'FollowingTimeline', id: args?.fromPostId }]
        },
        async queryFn(
          { hideReplies, fromPostId, hiddenWords },
          _api,
          _extraOptions,
          baseQuery
        ) {
          const timelineRes = await baseQuery({
            url: pathApiPostsFollowingTimeline,
            params: {
              number: LOAD_NUMBER,
              ...(hideReplies && { only_root: true }),
              ...(fromPostId != '0' && { from_post_id: fromPostId }),
            },
          })

          const posts = (timelineRes?.data?.posts ?? []).filter((x: Post) => {
            const text = x?.text

            if (!text || text.length === 0) return true

            return !hiddenWords?.reduce?.(
              (prev, cur) => prev || text?.toLowerCase?.().includes?.(cur),
              false
            )
          }) as Post[]

          const postsWithGroupRelationship = await mapPostGroupStatuses({
            baseQuery,
            posts,
          })

          return {
            data: postsWithGroupRelationship,
          }
        },
        keepUnusedDataFor: 0,
      }),

      getGroupTimeline: build.query<
        Array<Post>,
        {
          groupId: string
          hideReplies: boolean
          fromPostId: string
          hiddenWords: Array<string>
          isLoggedIn?: boolean
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'GroupTimeline', id: args?.fromPostId }]
        },
        queryFn: async (
          { groupId, hideReplies, fromPostId, hiddenWords, isLoggedIn },
          _api,
          _extraOptions,
          baseQuery
        ) => {
          const postsGroupTimelineRes = await baseQuery({
            url: pathApiPostsGroupTimeline,
            params: {
              number: LOAD_NUMBER,
              group_id: groupId,
              only_root: hideReplies,
              ...(fromPostId != '0' && { from_post_id: fromPostId }),
            },
          })

          const _posts: Array<Post> = await mapPostThreadStatuses({
            baseQuery,
            posts: postsGroupTimelineRes?.data?.posts ?? [],
            isLoggedIn,
          })

          return {
            data: _posts?.filter?.((x: Post) => {
              const text = x?.text

              if (!text || text.length === 0) return true

              return !hiddenWords?.reduce?.(
                (prev, cur) => prev || text?.toLowerCase?.().includes?.(cur),
                false
              )
            }),
          }
        },
        keepUnusedDataFor: 0,
      }),

      getGroupTimelinePinned: build.query<
        Array<Post>,
        {
          groupId: string
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'GroupTimelinePinned', id: args?.groupId }]
        },
        query: ({ groupId }) => ({
          url: pathApiPostsGroupTimeline,
          params: {
            number: LOAD_NUMBER,
            group_id: groupId,
            only_root: false,
          },
        }),
        transformResponse: (response) => {
          return (response?.pinned_posts ?? [])?.map?.((x: Post) => {
            return {
              ...x,
              _pinned: true,
            }
          })
        },
        keepUnusedDataFor: 0,
      }),

      pinnedPostGroupUpdate: build.mutation<
        any,
        {
          postId: number
          groupId: number
        }
      >({
        query: ({ postId, groupId }) => ({
          url: pathApiPostsGroupPinnedPost,
          method: 'PUT',
          data: {
            post_id: postId,
            group_id: groupId,
          },
        }),
      }),

      pinnedPostGroupDelete: build.mutation<
        any,
        {
          groupId: number
        }
      >({
        query: ({ groupId }) => ({
          url: pathApiPostsGroupPinnedPost,
          method: 'DELETE',
          data: {
            group_id: groupId,
          },
        }),
      }),

      getUserTimeline: build.query<
        Array<Post>,
        {
          fromPostId: string
          userId: string
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'UserTimeline', id: args?.fromPostId }]
        },
        async queryFn({ fromPostId, userId }, _api, _extraOptions, baseQuery) {
          const timelineRes = await baseQuery({
            url: pathApiPostsUserTimeline,
            params: {
              number: LOAD_NUMBER,
              user_id: userId,
              ...(fromPostId != '0' && { from_post_id: fromPostId }),
            },
          })

          const posts = (timelineRes?.data?.posts ?? []) as Post[]

          const postsWithGroupRelationship = await mapPostGroupStatuses({
            baseQuery,
            posts,
          })

          return {
            data: postsWithGroupRelationship,
          }
        },
        keepUnusedDataFor: 0,
      }),

      getUserTimelinePinned: build.query<
        Array<Post>,
        {
          userId: string
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'UserTimelinePinned', id: args?.userId }]
        },
        query: ({ userId }) => ({
          url: pathApiPostsUserTimeline,
          params: {
            number: LOAD_NUMBER,
            user_id: userId,
          },
        }),
        transformResponse: (response) => {
          return (response?.pinned_posts ?? [])?.map?.((x: Post) => {
            return {
              ...x,
              _pinned: true,
            }
          })
        },
        keepUnusedDataFor: 0,
      }),

      pinnedPostUpdate: build.mutation<
        any,
        {
          postId: number
        }
      >({
        query: ({ postId }) => ({
          url: pathApiPostsPinnedPost,
          method: 'POST',
          data: {
            id: postId,
          },
        }),
      }),

      pinnedPostDelete: build.mutation<
        any,
        {
          postId: number
        }
      >({
        query: ({ postId }) => ({
          url: pathApiPostsPinnedPostShow({ postId }),
          method: 'DELETE',
        }),
      }),

      getHashtagTimeline: build.query<
        Array<Post>,
        {
          fromPostId: string
          hashtag: string
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'HashtagTimeline', id: args?.fromPostId }]
        },
        query: ({ fromPostId, hashtag }) => ({
          url: pathApiPostsTags({ hashtag }),
          params: {
            number: LOAD_NUMBER,
            ...(fromPostId != '0' && { from_post_id: fromPostId }),
          },
        }),
        transformResponse: (response, _meta) => {
          return response?.posts ?? []
        },
        keepUnusedDataFor: 0,
      }),

      getHotPostTimeline: build.query<
        {
          posts: Post[]
          has_more_hot_posts?: boolean
        },
        {
          isFollowingTimeline: boolean
          sharedInterests?: boolean
          reduceSelfie?: boolean
          number?: number
        }
      >({
        providesTags: ['HotPostTimeline'],
        query: ({
          isFollowingTimeline,
          sharedInterests,
          reduceSelfie,
          number,
        }) => ({
          url: isFollowingTimeline
            ? pathApiPostsFollowingTimeline
            : pathApiPostsTimeline,
          params: {
            mxn: number ?? 3,
            number: number ?? 3,
            order_by: 'hot_first',
            ...(typeof sharedInterests === 'boolean' && {
              shared_interest_categories: sharedInterests,
            }),
            reduce_selfie: reduceSelfie,
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getRecommendedTimeline: build.query<
        {
          next_page_value: number
          posts: Array<Post>
        },
        {
          en: number
          vn: number
          number: number
          shared_interest_categories: boolean
        }
      >({
        providesTags: ['RecommendedTimeline'],
        query: ({ en, vn, number, shared_interest_categories }) => ({
          url: pathApiPostsRecommendedTimeline,
          params: {
            en,
            vn,
            number,
            shared_interest_categories,
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getTimelineTestingTools: build.query<
        {
          next_page_value: number
          posts: Array<Post>
        },
        {
          fromPostId: string
          interestCategoryIds: Array<string>
          userAgeMin: string
          userAgeMax: string
          includeFollowedUsers?: boolean
        }
      >({
        providesTags: ['TimelineTestingTools'],
        queryFn: async (
          {
            fromPostId,
            interestCategoryIds,
            userAgeMin,
            userAgeMax,
            includeFollowedUsers,
          },
          _api,
          _extraOptions,
          baseQuery
        ) => {
          const postsToolsPostsTimelineRes = await baseQuery({
            url: pathApiToolsPostsTimeline,
            params: {
              number: LOAD_NUMBER,
              ...(fromPostId != '0' && { from_post_id: fromPostId }),
              ...(interestCategoryIds?.length > 0 && {
                interest_category_ids: interestCategoryIds,
              }),
              user_age_min: userAgeMin,
              user_age_max: userAgeMax,
              include_followed_users: includeFollowedUsers,
            },
          })

          const _posts: Array<Post> = await mapPostFeedbacks({
            baseQuery,
            posts: postsToolsPostsTimelineRes?.data?.posts ?? [],
          })

          return {
            data: {
              next_page_value:
                postsToolsPostsTimelineRes?.data?.next_page_value,
              posts: _posts,
            },
          }
        },
        keepUnusedDataFor: 0,
      }),

      postFeedback: build.mutation<
        Feedback,
        {
          postId: number
          feedback: PostFeedback
        }
      >({
        query: ({ postId, feedback }) => ({
          url: pathApiFeedbacks,
          method: 'POST',
          data: {
            post_id: postId,
            feedback,
          },
        }),
      }),
    }),
    overrideExisting: false,
  })

export const {
  endpoints,
  useGetPublicTimelineQuery,
  useGetNoReplyPublicTimelineQuery,
  useGetFollowingTimelineQuery,
  useGetGroupTimelineQuery,
  useGetGroupTimelinePinnedQuery,
  usePinnedPostGroupUpdateMutation,
  usePinnedPostGroupDeleteMutation,
  useGetUserTimelineQuery,
  useLazyGetUserTimelineQuery,
  useGetUserTimelinePinnedQuery,
  usePinnedPostUpdateMutation,
  usePinnedPostDeleteMutation,
  useGetHashtagTimelineQuery,
  useGetHotPostTimelineQuery,
  useGetRecommendedTimelineQuery,
  useGetTimelineTestingToolsQuery,
  usePostFeedbackMutation,
} = timelineApi
